import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, setDoc, getFirestore } from 'firebase/firestore';
import { ref, onValue } from 'firebase/database';
import { UNIVERSIDADES_EXANI, CATALOGO_CARRERAS_GENERICO, getCarrerasParaUniversidad, MODULOS_CENEVAL, AREAS_TRANSVERSALES, getTemaIdsParaModulos } from '../constants/universitiesData';
import { database } from '../firebase';

const asList = (value) => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== 'object') return [];
  return Object.entries(value).map(([id, item]) => ({
    ...(item && typeof item === 'object' ? item : { nombre: item }),
    id: item?.id || item?.carreraId || id,
  }));
};

// Mapa de nombres legibles (RTDB) → claves snake_case (MODULOS_CENEVAL)
const NOMBRE_A_KEY_MODULO = {
  'Premedicina': 'premedicina',
  'premedicina': 'premedicina',
  'Cálculo diferencial e integral': 'calculo',
  'cálculo diferencial e integral': 'calculo',
  'Calculo diferencial e integral': 'calculo',
  'Física': 'fisica',
  'física': 'fisica',
  'Fisica': 'fisica',
  'Ciencias de la salud': 'ciencias_salud',
  'ciencias de la salud': 'ciencias_salud',
  'Biología': 'biologia',
  'biología': 'biologia',
  'Biologia': 'biologia',
  'Química': 'quimica',
  'química': 'quimica',
  'Quimica': 'quimica',
  'Psicología': 'psicologia',
  'psicología': 'psicologia',
  'Psicologia': 'psicologia',
  'Administración': 'administracion',
  'administración': 'administracion',
  'Administracion': 'administracion',
  'Economía': 'economia',
  'economía': 'economia',
  'Economia': 'economia',
  'Derecho': 'derecho',
  'derecho': 'derecho',
  'Historia': 'historia',
  'historia': 'historia',
  'Literatura': 'literatura',
  'literatura': 'literatura',
  'Probabilidad y estadística': 'probabilidad_estadistica',
  'probabilidad y estadística': 'probabilidad_estadistica',
  'Probabilidad y estadistica': 'probabilidad_estadistica',
  'Matemáticas financieras': 'matematicas_financieras',
  'matemáticas financieras': 'matematicas_financieras',
  'Matematicas financieras': 'matematicas_financieras',
  'Filosofía': 'filosofia',
  'filosofía': 'filosofia',
  'Filosofia': 'filosofia',
  'Ciencias sociales': 'ciencias_sociales',
  'ciencias sociales': 'ciencias_sociales',
  'Aritmética': 'aritmetica',
  'aritmética': 'aritmetica',
  'Aritmetica': 'aritmetica',
  'Ciencias experimentales': 'ciencias_experimentales',
  'ciencias experimentales': 'ciencias_experimentales',
  'Matemáticas': 'matematicas',
  'matemáticas': 'matematicas',
};

const toModuloKey = (raw) => {
  if (!raw) return null;
  // Ya es una clave snake_case válida
  if (/^[a-z][a-z0-9_]*$/.test(raw)) return raw;
  // Coincidencia exacta en el mapa
  if (NOMBRE_A_KEY_MODULO[raw]) return NOMBRE_A_KEY_MODULO[raw];
  // Fallback: normalizar quitando acentos, minúsculas y espacios → guiones bajos
  const sinAcentos = raw.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const clave = sinAcentos.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
  return clave || null;
};

const normalizeCareer = (career, fallbackId) => {
  // Leer desde cualquiera de los campos posibles, incluyendo modulos_exani_ii (RTDB)
  const rawModules = career.modulosSugeridos || career.modulosRequeridos || career.modulos || career.modules || career.modulos_exani_ii || [];
  const normalizedModules = asList(rawModules).map((module) => {
    const raw = typeof module === 'string' ? module : module.id || module.codigo || module.key || '';
    return toModuloKey(raw);
  }).filter(Boolean);

  return {
    ...career,
    id: career.id || career.carreraId || career.codigo || fallbackId,
    nombre: career.nombre || career.name || career.carreraNombre || career.title || fallbackId,
    categoria: career.categoria || career.area || career.category || 'General',
    modulosSugeridos: normalizedModules,
  };
};

const normalizeCatalog = (raw) => {
  if (!raw || typeof raw !== 'object') return [];

  const universitySource = raw.universidades || raw.instituciones || raw.institutions || raw;
  const careersByUniversity = raw.carreras || raw.careers || raw.universidadesCarreras || {};

  return asList(universitySource)
    .filter((item) => item && (item.nombre || item.name || item.universidadNombre || item.abreviatura || item.codigo))
    .map((item) => {
      const id = item.id || item.universidadId || item.codigo;
      const nestedCareers = item.carreras || item.careers || item.ofertaAcademica || item.oferta_academica;
      const linkedCareers = careersByUniversity[id] || careersByUniversity[item.codigo] || [];
      const careers = asList(nestedCareers || linkedCareers).map((career, index) => normalizeCareer(career, `${id}-carrera-${index + 1}`));

      return {
        ...item,
        id,
        nombre: item.nombre || item.name || item.universidadNombre || id,
        abreviatura: item.abreviatura || item.siglas || item.shortName || item.codigo || id,
        estado: item.estado || item.state || item.entidad || 'México',
        campus: item.campus || item.sede_principal || item.sede || item.plantel || (item.estado ? `Campus ${item.estado}` : 'Campus Central'),
        carreras: careers,
      };
    })
    .filter((item) => item.id);
};

const buildPlanForCareer = (career) => {
  const moduleIds = career?.modulosSugeridos || [];
  const specificModules = moduleIds.map((moduleId) => {
    const module = MODULOS_CENEVAL[moduleId];
    return module
      ? { ...module, obligatorio: true }
      : { id: moduleId, nombre: moduleId.replace(/_/g, ' '), reactivos: 24, obligatorio: true };
  });

  return {
    transversales: AREAS_TRANSVERSALES.map((area) => ({ ...area, obligatorio: true })),
    especificos: specificModules,
    totalReactivos: 90 + specificModules.reduce((total, module) => total + module.reactivos, 0),
    esPremedicina: moduleIds.includes('premedicina'),
    temaIdsPermitidos: getTemaIdsParaModulos(moduleIds),
  };
};

const staticCatalog = () => UNIVERSIDADES_EXANI.map((university) => ({
  ...university,
  carreras: getCarrerasParaUniversidad(university.id),
}));

const UniversityCareerSelection = ({ user, onComplete }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState('universidad'); // 'universidad' | 'carrera' | 'confirm'
  const [universidadId, setUniversidadId] = useState('');
  const [carreraId, setCarreraId] = useState('');
  const [searchUni, setSearchUni] = useState('');
  const [searchCarrera, setSearchCarrera] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAllUnis, setShowAllUnis] = useState(false);
  const [showAllCarreras, setShowAllCarreras] = useState(false);
  const [universidades, setUniversidades] = useState(staticCatalog);

  useEffect(() => {
    const unsubscribe = onValue(ref(database, 'universidades_exani_ii'), (snapshot) => {
      const catalog = normalizeCatalog(snapshot.val());
      setUniversidades(catalog.length > 0 ? catalog : staticCatalog());
    }, () => setUniversidades(staticCatalog()));

    return () => unsubscribe();
  }, []);

  const catalogUniversidades = universidades;

  const filteredUniversidades = catalogUniversidades.filter(u => {
    if (!searchUni.trim()) return showAllUnis ? catalogUniversidades : catalogUniversidades.slice(0, 10);
    const q = searchUni.toLowerCase();
    return u.nombre.toLowerCase().includes(q) ||
           u.abreviatura.toLowerCase().includes(q) ||
           u.id.toLowerCase().includes(q) ||
           u.estado.toLowerCase().includes(q);
  });

  const selectedUniversidad = catalogUniversidades.find(u => u.id === universidadId);
  const carrerasDisponibles = selectedUniversidad?.carreras?.length
    ? selectedUniversidad.carreras
    : (universidadId ? getCarrerasParaUniversidad(universidadId) : CATALOGO_CARRERAS_GENERICO);
  
  const filteredCarreras = carrerasDisponibles.filter(c => {
    if (!searchCarrera.trim()) return showAllCarreras ? carrerasDisponibles : carrerasDisponibles.slice(0, 15);
    const q = searchCarrera.toLowerCase();
    return c.nombre.toLowerCase().includes(q) ||
           c.id.toLowerCase().includes(q) ||
           c.categoria?.toLowerCase().includes(q);
  });

  const selectedCarrera = carrerasDisponibles.find(c => c.id === carreraId);
  const handleSaveSelection = async () => {
    if (!universidadId || !carreraId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const firestore = getFirestore();
      const timestamp = Date.now();
      
      // Datos a guardar
      const userProfileData = {
        uid: user.uid,
        email: user.email || '',
        displayName: user.displayName || '',
        photoURL: user.photoURL || '',
        universidadId,
        universidadNombre: selectedUniversidad?.nombre,
        universidadAbreviatura: selectedUniversidad?.abreviatura,
        campus: selectedUniversidad?.campus || selectedUniversidad?.sede_principal || selectedUniversidad?.sede || (selectedUniversidad?.estado ? `Campus ${selectedUniversidad.estado}` : 'Campus Central'),
        carreraId,
        carreraNombre: selectedCarrera?.nombre,
        carreraCategoria: selectedCarrera?.categoria,
        modulosRequeridos: selectedCarrera?.modulosSugeridos || [],
        planEstudios: buildPlanForCareer(selectedCarrera),
        configuradoEn: new Date().toISOString(),
        configuradoTimestamp: timestamp,
        versionConfig: '2027-1',
        onboardingCompleted: true,
        updatedAt: timestamp
      };

      // 1. Guardar en Firestore (users/{uid})
      await setDoc(doc(firestore, 'users', user.uid), userProfileData, { merge: true });

      console.log('✅ Universidad y carrera guardadas:', userProfileData);
      
      if (onComplete) {
        onComplete(userProfileData);
      } else {
        navigate('/dashboard', { replace: true });
      }
      
    } catch (err) {
      console.error('Error guardando selección:', err);
      setError('Error al guardar la selección. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    if (onComplete) onComplete(null);
    else navigate('/dashboard', { replace: true });
  };

  const renderUniversidadStep = () => (
    <div className="selection-step">
      <div className="step-header">
        <div className="step-indicator">
          <span className="step-number active">1</span>
          <span className="step-label">Universidad</span>
          <span className="step-connector" />
          <span className="step-number">2</span>
          <span className="step-label">Carrera</span>
          <span className="step-connector" />
          <span className="step-number">3</span>
          <span className="step-label">Confirmar</span>
        </div>
      </div>

      <div className="step-content">
        <h2>¿En qué universidad presentarás el EXANI-II?</h2>
        <p className="step-subtitle">Busca por nombre, abreviatura o estado</p>

        <div className="search-box">
          <input
            type="text"
            placeholder="Ej: UANL, Veracruz, Panamericana..."
            value={searchUni}
            onChange={(e) => setSearchUni(e.target.value)}
            autoFocus
            className="search-input"
          />
          <span className="search-icon">🔍</span>
        </div>

        <div className="results-info">
          {searchUni ? `${filteredUniversidades.length} universidades encontradas` : 
           `${UNIVERSIDADES_EXANI.length} universidades disponibles${!showAllUnis ? ` (mostrando 10)` : ''}`}
        </div>

        <div className="universidades-grid">
          {filteredUniversidades.map(uni => (
            <button
              key={uni.id}
              className={`university-card ${universidadId === uni.id ? 'selected' : ''}`}
              onClick={() => { setUniversidadId(uni.id); setSearchUni(''); }}
            >
              <div className="uni-header">
                <span className="uni-abreviatura">{uni.abreviatura}</span>
                {uni.militar && <span className="badge militar">🎖️ Militar</span>}
                {uni.privada && <span className="badge privada">🏛️ Privada</span>}
                {uni.personalizada && <span className="badge otra">✏️ Otra</span>}
              </div>
              <div className="uni-nombre">{uni.nombre}</div>
              <div className="uni-estado">{uni.estado}</div>
            </button>
          ))}
        </div>

        {!searchUni && filteredUniversidades.length < UNIVERSIDADES_EXANI.length && (
          <button className="show-all-btn" onClick={() => setShowAllUnis(!showAllUnis)}>
            {showAllUnis ? 'Mostrar menos' : `Ver todas (${UNIVERSIDADES_EXANI.length})`}
          </button>
        )}

        {universidadId && (
          <div className="step-navigation">
            <button className="btn-secondary" onClick={() => setUniversidadId('')}>Cambiar</button>
            <button className="btn-primary" onClick={() => setStep('carrera')}>Continuar →</button>
          </div>
        )}
      </div>
    </div>
  );

  const renderCarreraStep = () => (
    <div className="selection-step">
      <div className="step-header">
        <div className="step-indicator">
          <span className="step-number done">✓</span>
          <span className="step-label">Universidad</span>
          <span className="step-connector" />
          <span className="step-number active">2</span>
          <span className="step-label">Carrera</span>
          <span className="step-connector" />
          <span className="step-number">3</span>
          <span className="step-label">Confirmar</span>
        </div>
        <div className="selected-uni-summary">
          🏫 {selectedUniversidad?.nombre} ({selectedUniversidad?.abreviatura})
        </div>
      </div>

      <div className="step-content">
        <h2>¿Qué carrera deseas estudiar?</h2>
        <p className="step-subtitle">Se muestran las carreras disponibles según los módulos EXANI-II</p>

        <div className="search-box">
          <input
            type="text"
            placeholder="Ej: Medicina, Ingeniería, Derecho, Psicología..."
            value={searchCarrera}
            onChange={(e) => setSearchCarrera(e.target.value)}
            autoFocus
            className="search-input"
          />
          <span className="search-icon">🔍</span>
        </div>

        <div className="results-info">
          {searchCarrera ? `${filteredCarreras.length} carreras encontradas` :
           `${carrerasDisponibles.length} carreras disponibles${!showAllCarreras ? ` (mostrando 15)` : ''}`}
        </div>

        <div className="carreras-grid">
          {filteredCarreras.map(carrera => (
            <button
              key={carrera.id}
              className={`carrera-card ${carreraId === carrera.id ? 'selected' : ''}`}
              onClick={() => { setCarreraId(carrera.id); setSearchCarrera(''); }}
            >
              <div className="carrera-header">
                <span className="carrera-icon">{carrera.categoria === 'Salud' ? '🏥' : carrera.categoria === 'Ingenierías' ? '⚙️' : carrera.categoria === 'Económico-Administrativas' ? '💼' : '📚'}</span>
                <span className="carrera-categoria">{carrera.categoria}</span>
              </div>
              <div className="carrera-nombre">{carrera.nombre}</div>
              <div className="carrera-modulos">
                {(carrera.modulosSugeridos || []).map(m => (
                  <span key={m} className="modulo-tag">{m.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                ))}
              </div>
              {carrera.noCombinable && <span className="badge especial">⚠️ Módulo único (48 reactivos)</span>}
            </button>
          ))}
        </div>

        {!searchCarrera && filteredCarreras.length < carrerasDisponibles.length && (
          <button className="show-all-btn" onClick={() => setShowAllCarreras(!showAllCarreras)}>
            {showAllCarreras ? 'Mostrar menos' : `Ver todas (${carrerasDisponibles.length})`}
          </button>
        )}

        <div className="step-navigation">
          <button className="btn-secondary" onClick={() => { setStep('universidad'); setCarreraId(''); }}>← Volver</button>
          {carreraId && <button className="btn-primary" onClick={() => setStep('confirm')}>Continuar →</button>}
        </div>
      </div>
    </div>
  );

  const renderConfirmStep = () => {
    const plan = buildPlanForCareer(selectedCarrera);
    
    return (
      <div className="selection-step">
        <div className="step-header">
          <div className="step-indicator">
            <span className="step-number done">✓</span>
            <span className="step-label">Universidad</span>
            <span className="step-connector" />
            <span className="step-number done">✓</span>
            <span className="step-label">Carrera</span>
            <span className="step-connector" />
            <span className="step-number active">3</span>
            <span className="step-label">Confirmar</span>
          </div>
        </div>

        <div className="step-content confirm-content">
          <h2>Confirmar tu selección</h2>
          <p className="step-subtitle">Tu ruta de estudio se configurará automáticamente</p>

          <div className="confirm-card">
            <div className="confirm-item">
              <span className="confirm-label">Universidad</span>
              <span className="confirm-value">{selectedUniversidad?.nombre} ({selectedUniversidad?.abreviatura})</span>
            </div>
            <div className="confirm-item">
              <span className="confirm-label">Carrera</span>
              <span className="confirm-value">{selectedCarrera?.nombre}</span>
            </div>
            <div className="confirm-item">
              <span className="confirm-label">Módulos específicos</span>
              <span className="confirm-value">
                {selectedCarrera?.modulosSugeridos?.map(m => m.replace(/_/g, ' ')).join(' + ') || 'Por definir'}
              </span>
            </div>
            <div className="confirm-item">
              <span className="confirm-label">Total reactivos</span>
              <span className="confirm-value">{plan.totalReactivos} ({plan.esPremedicina ? 'Incluye Premedicina (48)' : '2 módulos de 24'})</span>
            </div>
          </div>

          <div className="plan-preview">
            <h4>📋 Tu plan de estudios incluirá:</h4>
            <div className="plan-sections">
              <div className="plan-section">
                <span className="section-title">Base Transversal (Obligatoria - 90 reactivos)</span>
                <ul>
                  {plan.transversales.map(t => (
                    <li key={t.id}>{t.nombre} ({t.reactivos} reactivos)</li>
                  ))}
                </ul>
              </div>
              <div className="plan-section">
                <span className="section-title">Módulos Específicos ({plan.especificos.length} módulo{plan.especificos.length !== 1 ? 's' : ''})</span>
                <ul>
                  {plan.especificos.map(m => (
                    <li key={m.id}>{m.nombre} ({m.reactivos} reactivos)</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="step-navigation">
            <button className="btn-secondary" onClick={() => setStep('carrera')}>← Cambiar carrera</button>
            <button 
              className="btn-primary btn-large" 
              onClick={handleSaveSelection}
              disabled={loading}
            >
              {loading ? '⏳ Guardando...' : '✅ Confirmar y empezar a estudiar'}
            </button>
          </div>

          <button className="skip-btn" onClick={handleSkip}>
            Saltar configuración (configurar después)
          </button>

          {error && <div className="error-message">{error}</div>}
        </div>
      </div>
    );
  };

  return (
    <div className="university-career-selection">
      <div className="selection-container">
        <div className="selection-header">
          <div className="logo">🎓</div>
          <h1>Vortex EXANI-II</h1>
          <p>Configura tu preparación personalizada</p>
        </div>

        {step === 'universidad' && renderUniversidadStep()}
        {step === 'carrera' && renderCarreraStep()}
        {step === 'confirm' && renderConfirmStep()}
      </div>
    </div>
  );
};

export default UniversityCareerSelection;